import { Box, Collapse, Group, Text, UnstyledButton } from '@mantine/core'
import { IconChevronRight } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { useId, useState } from 'react'

import { theme } from '@/configs/theme'
import * as classes from '@/styles/AdvancedDisclosure.css'

type AdvancedDisclosureProps = {
  /** Names what is inside, so the expert can decide without opening it. */
  summary: string
  /**
   * Opens the section and keeps it open. For a validation error on a field in here:
   * `Collapse` leaves closed content `display: none`, so an error the expert can't see
   * is one they can't fix, and scrolling to it does nothing.
   */
  revealed?: boolean
  /**
   * Opens the section without holding it open, for a condition the expert cannot clear by
   * editing the field. A failed generation is the case: unlike a validation error, nothing
   * resets it on a keystroke, so feeding it to `revealed` pins the section open for the rest
   * of the session and "Hide Advanced" stops working.
   */
  latchOpen?: boolean
  children: ReactNode
}

/**
 * The Advanced section SME-1098 puts in the file modal and the module form. Always
 * starts closed — her spec is explicit that these are fields most experts never touch,
 * and a disclosure that remembers being open stops doing its job.
 */
export function AdvancedDisclosure({ summary, revealed, latchOpen, children }: AdvancedDisclosureProps) {
  const [open, setOpen] = useState(!!(revealed || latchOpen))
  const contentId = useId()

  // Latched, then held. Mantine clears a field's error on the first keystroke, so a reveal
  // that only lasted as long as the error collapsed the section around the input the expert
  // had just been told to fix. Latching keeps it open through the fix; the `revealed` term
  // stays so the section cannot be put back out of sight while the error is still there.
  const [previousTriggers, setPreviousTriggers] = useState({ revealed, latchOpen })
  if (revealed !== previousTriggers.revealed || latchOpen !== previousTriggers.latchOpen) {
    setPreviousTriggers({ revealed, latchOpen })
    if (revealed || latchOpen) setOpen(true)
  }

  const opened = open || !!revealed

  return (
    <Box>
      <UnstyledButton
        className={classes.trigger}
        onClick={() => setOpen(o => !o)}
        aria-expanded={opened}
        aria-controls={contentId}>
        <Group gap={theme.rem(6)} wrap="nowrap">
          <IconChevronRight
            size={16}
            className={theme.cx(classes.chevron, opened && classes.chevronOpen)}
            aria-hidden="true"
          />
          <Text fw={600} size="sm">
            Advanced
          </Text>
          <Text c="dimmed" size="sm">
            {opened ? 'Hide' : 'Show'} {summary}
          </Text>
        </Group>
      </UnstyledButton>

      <Collapse expanded={opened} id={contentId}>
        <Box pt="md">{children}</Box>
      </Collapse>
    </Box>
  )
}
